import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericView, SearchInput, ActionButtons } from '../../../components/common/table';

const CustomerBalance = ({
    customerBalanceData,
    salesListDetailData,
    isLoading,
    searchQueryCustomerBalance,
    setSearchQueryCustomerBalance,
    handleExportExcelCustomerBalance,
    formatCurrencyValue,
    filters
}) => {
    const navigate = useNavigate();

    // Function to navigate to invoice page with filters
    const navigateToInvoice = (status = null) => {
        // Get date range from filters (assuming filters has period and date range info)
        let dateRange = { startDate: null, endDate: null };

        if (filters && filters.period) {
            // Import the date range calculation function (or inline it)
            const getDateRangeFromPeriod = (period, customStartDate, customEndDate) => {
                if (period === 'custom' && customStartDate && customEndDate) {
                    return { startDate: customStartDate, endDate: customEndDate };
                }

                const now = new Date();
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth();

                let startDate, endDate;

                switch (period) {
                    case 'this_year':
                        startDate = new Date(currentYear, 0, 1);
                        endDate = new Date(currentYear, 11, 31);
                        break;
                    case 'last_year':
                        startDate = new Date(currentYear - 1, 0, 1);
                        endDate = new Date(currentYear - 1, 11, 31);
                        break;
                    case 'this_month':
                        startDate = new Date(currentYear, currentMonth, 1);
                        endDate = new Date(currentYear, currentMonth + 1, 0);
                        break;
                    case 'last_month':
                        startDate = new Date(currentYear, currentMonth - 1, 1);
                        endDate = new Date(currentYear, currentMonth, 0);
                        break;
                    case 'this_quarter':
                        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
                        startDate = new Date(currentYear, quarterStartMonth, 1);
                        endDate = new Date(currentYear, quarterStartMonth + 3, 0);
                        break;
                    case 'last_quarter':
                        const lastQuarterStartMonth = Math.floor(currentMonth / 3) * 3 - 3;
                        startDate = new Date(currentYear, lastQuarterStartMonth, 1);
                        endDate = new Date(currentYear, lastQuarterStartMonth + 3, 0);
                        break;
                    default:
                        // Default to this year
                        startDate = new Date(currentYear, 0, 1);
                        endDate = new Date(currentYear, 11, 31);
                }

                // Format dates as YYYY-MM-DD
                const formatDate = (date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                return {
                    startDate: formatDate(startDate),
                    endDate: formatDate(endDate)
                };
            };

            dateRange = getDateRangeFromPeriod(
                filters.period,
                filters.startDate,
                filters.endDate
            );
        }

        // Build query parameters
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('billDateFrom', dateRange.startDate);
        if (dateRange.endDate) params.append('billDateTo', dateRange.endDate);

        // Carry over the active Sales Creator filter — maps to Invoice's "Created By" filter
        // (Invoice's own assignedTo param filters by the linked Project.assignedTo, a different field).
        if (Array.isArray(filters?.salesCreator) && filters.salesCreator.length > 0) {
            params.append('createdById', filters.salesCreator[0]);
        }

        // If status is provided, add status filter
        if (status) {
            // Handle both single status and array of statuses
            if (Array.isArray(status)) {
                status.forEach(s => params.append('status', s));
            } else {
                params.append('status', status);
            }
        }

        // Navigate to invoice page with query parameters
        navigate(`/invoice?${params.toString()}`);
    };

    // Filter out total row for calculations
    const dataWithoutTotal = customerBalanceData.filter(item => item.customerName !== 'GRAND TOTAL');

    // Calculate metrics
    const metrics = useMemo(() => {
        // Total Piutang Belum Dibayar (Total Outstanding Debt) - still from customer balance data
        const totalOutstanding = dataWithoutTotal
            .filter(item => item.status === 'Belum Dibayar' || item.status === 'Pembayaran Partial')
            .reduce((sum, item) => sum + (item.sisaPiutang || 0), 0);

        // Invoice counts from individual invoice data (salesListDetailData)
        const paidInvoices = salesListDetailData.filter(item => item.statusBayar === 'Paid');
        const paidInvoiceCount = paidInvoices.length;

        const partialInvoices = salesListDetailData.filter(item => item.statusBayar === 'Partial');
        const partialInvoiceCount = partialInvoices.length;

        const unpaidInvoices = salesListDetailData.filter(item => item.statusBayar === 'Unpaid');
        const outstandingInvoiceCount = unpaidInvoices.length;

        // Pembayaran Tepat Waktu (%) - Percentage of total paid vs total billed
        const totalTagihan = dataWithoutTotal.reduce((sum, item) => sum + (item.totalTagihan || 0), 0);
        const totalTerbayar = dataWithoutTotal.reduce((sum, item) => sum + (item.totalTerbayar || 0), 0);
        const onTimePaymentPercentage = totalTagihan > 0 ? (totalTerbayar / totalTagihan) * 100 : 0;

        // Nilai Pembayaran Partial (Partial Payment Value) - still from customer balance data
        const partialPaymentValue = dataWithoutTotal
            .filter(item => item.status === 'Pembayaran Partial')
            .reduce((sum, item) => sum + (item.totalTagihan || 0), 0);

        // Jumlah Pelanggan Belum Lunas (Number of Unpaid Customers)
        const unpaidCustomersCount = dataWithoutTotal
            .filter(item => item.status === 'Belum Dibayar' || item.status === 'Pembayaran Partial')
            .length;

        return {
            totalOutstanding,
            outstandingInvoiceCount,
            onTimePaymentPercentage,
            paidInvoiceCount,
            partialPaymentValue,
            partialInvoiceCount,
            unpaidCustomersCount
        };
    }, [dataWithoutTotal, salesListDetailData]);

    // Filter data by search query
    const filteredCustomerBalanceData = customerBalanceData.filter(item => {
        if (!searchQueryCustomerBalance) return true;
        const query = searchQueryCustomerBalance.toLowerCase();
        return (
            item.customerName?.toLowerCase().includes(query) ||
            item.status?.toLowerCase().includes(query)
        );
    });

    // Table columns configuration
    const customerBalanceColumns = [
        {
            key: 'customerName',
            label: 'CUSTOMER NAME',
            className: 'text-truncate',
            sortValue: (item) => item.customerName || '',
            render: (customerName, row) => {
                const isTotal = customerName === 'GRAND TOTAL';
                if (!isTotal && row.companyId) {
                    return (
                        <a
                            href={`/clients/${row.companyId}`}
                            className="text-primary text-truncate d-inline-block text-decoration-none"
                            style={{ cursor: 'pointer', maxWidth: '250px', width: '250px' }}
                            title={customerName}
                            onClick={(e) => {
                                if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                e.preventDefault();
                                navigate(`/clients/${row.companyId}`);
                            }}
                            onAuxClick={(e) => {
                                if (e.button === 1) e.stopPropagation();
                            }}
                        >
                            {customerName || '-'}
                        </a>
                    );
                }
                return (
                    <span
                        className={`text-truncate d-inline-block ${isTotal ? 'fw-bold' : ''}`}
                        style={{ maxWidth: '250px', width: '250px' }}
                        title={customerName}
                    >
                        {customerName || '-'}
                    </span>
                );
            },
            sortable: true
        },
        {
            key: 'totalTagihan',
            label: 'TOTAL BILLED (IDR)',
            className: 'text-truncate',
            sortValue: (item) => item.totalTagihan || 0,
            render: (totalTagihan, row) => {
                const isTotal = row.customerName === 'GRAND TOTAL';
                const formattedValue = isTotal ? '' : `Rp ${formatCurrencyValue(totalTagihan || 0)}`;
                return (
                    <span
                        className={`text-truncate d-inline-block ${isTotal ? 'fw-bold' : ''}`}
                        style={{ textAlign: 'right', maxWidth: '180px', width: '180px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            },
            sortable: true
        },
        {
            key: 'totalTerbayar',
            label: 'TOTAL PAID (IDR)',
            className: 'text-truncate',
            sortValue: (item) => item.totalTerbayar || 0,
            render: (totalTerbayar, row) => {
                const isTotal = row.customerName === 'GRAND TOTAL';
                const formattedValue = isTotal ? '' : `Rp ${formatCurrencyValue(totalTerbayar || 0)}`;
                return (
                    <span
                        className={`text-truncate d-inline-block ${isTotal ? 'fw-bold' : ''}`}
                        style={{ textAlign: 'right', maxWidth: '180px', width: '180px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            },
            sortable: true
        },
        {
            key: 'sisaPiutang',
            label: 'OUTSTANDING RECEIVABLE (IDR)',
            className: 'text-truncate',
            sortValue: (item) => item.sisaPiutang || 0,
            render: (sisaPiutang, row) => {
                const isTotal = row.customerName === 'GRAND TOTAL';
                const hasOutstanding = sisaPiutang > 0;
                const formattedValue = `Rp ${formatCurrencyValue(sisaPiutang || 0)}`;
                return (
                    <span
                        className={`text-truncate d-inline-block ${isTotal ? 'fw-bold' : ''} ${hasOutstanding ? 'text-danger' : ''}`}
                        style={{ textAlign: 'right', maxWidth: '180px', width: '180px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            },
            sortable: true
        },
        {
            key: 'status',
            label: 'PAYMENT STATUS',
            className: 'text-truncate',
            sortValue: (item) => item.status || '',
            render: (status, row) => {
                const isTotal = row.customerName === 'GRAND TOTAL';
                if (isTotal) {
                    return (
                        <span
                            className="text-truncate d-inline-block fw-bold"
                            style={{ maxWidth: '150px', width: '150px' }}
                        >
                        </span>
                    );
                }

                let statusClass = 'text-success'; // Lunas (green)
                if (status === 'Belum Dibayar') {
                    statusClass = 'text-warning'; // Not Paid (orange)
                } else if (status === 'Pembayaran Partial') {
                    statusClass = 'text-warning'; // Partial Payment (orange)
                }

                return (
                    <span
                        className={`text-truncate d-inline-block ${statusClass}`}
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={status}
                    >
                        {status || '-'}
                    </span>
                );
            },
            sortable: true
        }
    ];

    return (
        <>
            {/* Title */}
            <h3 className="mb-3">Customer Accounts Receivable Report (Customer Balance Report)</h3>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-danger"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Total Unpaid Receivables</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.totalOutstanding)}</div>
                                <div
                                    className="h3 text-danger"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice(['SENT', 'FINAL'])}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view unpaid invoices"
                                >
                                    {metrics.outstandingInvoiceCount} Invoice
                                </div>
                            </div>

                            <div className="text-muted small">Total value of outstanding unpaid invoice balances.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-success"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">On-Time Payment Rate (%)</div>
                                <div className="h3 mb-0">{metrics.onTimePaymentPercentage.toFixed(1)}%</div>
                                <div
                                    className="h3 text-success"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice('PAID')}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view paid invoices"
                                >
                                    {metrics.paidInvoiceCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Percentage of total amount paid compared to total billed amount.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-warning"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Partial Payment Amount</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.partialPaymentValue)}</div>
                                <div
                                    className="h3 text-warning"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice('PARTIAL')}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view partial invoices"
                                >
                                    {metrics.partialInvoiceCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Total value of payments received from partially paid invoices.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-secondary"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Number of Customers with Outstanding Balances</div>
                                <div className="h3 mb-0">{metrics.unpaidCustomersCount} Customers</div>
                            </div>
                            <div className="text-muted small">Total outstanding balance to be collected (Outstanding AR).</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="card">
                <div className="card-header">
                    <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                        <li className="nav-item-tab" role="presentation">
                            <a href="#tabs-list-data-customer" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                        </li>
                    </ul>
                </div>
                <div className="card-body">
                    <div className="tab-content">
                        <div className="tab-pane active show" id="tabs-list-data-customer" role="tabpanel">
                            <GenericView
                                data={filteredCustomerBalanceData}
                                columns={customerBalanceColumns}
                                isLoading={isLoading}
                                emptyStateMessage="No customer balance data found"
                                searchComponent={
                                    <SearchInput
                                        value={searchQueryCustomerBalance}
                                        onChange={setSearchQueryCustomerBalance}
                                        placeholder="Search customer name or status..."
                                    />
                                }
                                actionButtons={
                                    <ActionButtons
                                        onExportExcel={handleExportExcelCustomerBalance}
                                    />
                                }
                                rowClassName={(row) => {
                                    return row.customerName === 'GRAND TOTAL' ? 'table-active fw-bold' : '';
                                }}
                                isRowSelectable={(row) => {
                                    return row.customerName !== 'GRAND TOTAL';
                                }}
                                showPagination={true}
                                initialPageSize={10}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomerBalance;

